import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DATE_FORMAT_DAY, DATE_FORMAT_MONTH_YEAR, ReportStatuses } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { CrossIcon, EditIcon, IncompleteSvg, Loading, SquareCard } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { ModalLarge } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { useReport } from '../../../context/report/WithReport';
import { useRouter } from '../../../context/router/useRouter';
import { allowRenderReports } from '../../../helpers/renderRoutesByPermissions';
import useAppLocation from '../../../hooks/useAppLocation';
import usePrevious from '../../../hooks/usePrevious';
import { useAppDispatch } from '../../../redux/hooks';
import { ReportsPreviewActions } from './ReportsPreviewActions';
import { ReportsPreviewContent } from './ReportsPreviewContent';

import './ReportsPreviewModal.scss';

export const ReportsPreviewModal: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { permissions } = useAuth();
  const { closeAndClean } = useRouter();

  const { report, isFetching, onChangeStatus, isChangingStatus, canEdit } = useReport();

  const prevFetching = usePrevious(isFetching);
  const { $t } = useIntl();
  const userHasAccess = allowRenderReports(permissions);

  const showEditButton = canEdit;

  const handleEditButtonClick = useCallback(() => {
    navigate(`/reports/${report?.id}/edit`, {
      state: { ...location.state, replace: report?.report_status === ReportStatuses.Draft },
    });
  }, [location.state, navigate, report?.id, report?.report_status]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    if (prevFetching && !isFetching && !report) {
      navigate('/reports');
    }
  }, [dispatch, isFetching, navigate, prevFetching, report]);

  const date = newDateTimezoneOffset(report?.scheduled_publish_date);
  const assessmentDay = format(date, DATE_FORMAT_DAY);
  const assessmentMonthYear = format(date, DATE_FORMAT_MONTH_YEAR);

  const renderContent = () => {
    if (!report)
      return (
        <Box position="absolute" width="100%" height="100%">
          <Loading />
        </Box>
      );

    return (
      <>
        <ModalHeaderV2
          title={
            <Stack direction="row" gap={2.5} alignItems="center">
              {date && (
                <SquareCard sx={{ '.MuiTypography-root': { color: 'common.grey2' } }}>
                  <Typography mt={0.25} variant="h2">
                    {assessmentDay}
                  </Typography>
                  <Typography variant="caption">{assessmentMonthYear}</Typography>
                </SquareCard>
              )}

              <Typography variant="inherit">{report.name}</Typography>
            </Stack>
          }
          withGoBack
          active
        >
          <ReportsPreviewActions
            isLoading={isChangingStatus}
            onClick={onChangeStatus}
            report={report}
          />
          {showEditButton && (
            <IconButton onClick={handleEditButtonClick} data-test-id="report-modal-edit-button">
              <EditIcon />
            </IconButton>
          )}

          <IconButton onClick={closeAndClean} data-test-id="report-modal-close-button">
            <CrossIcon />
          </IconButton>
        </ModalHeaderV2>
        {report.report_status === ReportStatuses.Draft ? (
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              gap: theme.spacing(2.5),
            })}
          >
            <IncompleteSvg />
            <Typography
              variant="h3"
              color="text.primary"
              sx={{ textAlign: 'center', maxWidth: 300 }}
            >
              {$t({ id: 'reports-NotConfigured' })}
            </Typography>
            {showEditButton && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => navigate('edit', { state: { replace: true } })}
              >
                {$t({ id: 'action-ContinueSetup' })}
              </Button>
            )}
          </Stack>
        ) : (
          <ReportsPreviewContent report={report} />
        )}
      </>
    );
  };

  return (
    <ModalLarge open onClose={closeAndClean}>
      {userHasAccess ? renderContent() : <AccessDenied />}
    </ModalLarge>
  );
};
